"use client"

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'

import { SHEET_CLOSE_DURATION_MS } from '@/components/ui/sheet'
import { setCanvasUiSurface, type UiSurface } from '@/lib/ui-surface'

import { EditableArtifactCanvasShell } from './editable-artifact-canvas-shell'

export interface ArtifactCanvasConfig {
  title: React.ReactNode
  content: React.ReactNode
  preventAutoFocus?: boolean
  /** Optional current object so the agent can participate while this canvas is open. */
  surface?: UiSurface
}

interface ArtifactCanvasContextValue {
  openCanvas: (config: ArtifactCanvasConfig) => void
  closeCanvas: () => void
  /** Update the current-object payload after a draft id is known. */
  setCanvasSurface: (surface: UiSurface | null) => void
  /** True when the shell is showing a canvas (dismissal can be observed for URL sync, etc.) */
  isOpen: boolean
}

const ArtifactCanvasContext = createContext<ArtifactCanvasContextValue | null>(null)

interface ArtifactCanvasProviderProps {
  children: React.ReactNode
  portalContainer: HTMLElement | null
}

/**
 * After a pathname change, close only if no child called `openCanvas` in the same
 * commit (child effects run before this parent effect). Deep links that open a
 * canvas on the new route bump the generation and keep the sheet open.
 */
export function shouldCloseCanvasAfterPathChange(
  openGenerationAtPathRender: number,
  openGenerationAfterChildEffects: number,
): boolean {
  return openGenerationAfterChildEffects === openGenerationAtPathRender
}

export function ArtifactCanvasProvider({
  children,
  portalContainer,
}: ArtifactCanvasProviderProps) {
  const { pathname } = useLocation()
  const [canvasConfig, setCanvasConfig] = useState<ArtifactCanvasConfig | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const openGenerationRef = useRef(0)
  const pathRenderGenerationRef = useRef(0)
  const prevPathnameRef = useRef(pathname)

  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname
    pathRenderGenerationRef.current = openGenerationRef.current
  }

  const closeCanvas = useCallback(() => {
    setSheetOpen(false)
    setCanvasUiSurface(null)
  }, [])

  const openCanvas = useCallback((config: ArtifactCanvasConfig) => {
    openGenerationRef.current += 1
    setCanvasConfig(config)
    setSheetOpen(true)
    setCanvasUiSurface(config.surface ?? null)
  }, [])

  const setCanvasSurface = useCallback((surface: UiSurface | null) => {
    setCanvasUiSurface(surface)
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSheetOpen(false)
      setCanvasUiSurface(null)
    }
  }, [])

  useEffect(() => {
    if (sheetOpen || !canvasConfig) return

    const timeout = window.setTimeout(() => {
      setCanvasConfig(null)
    }, SHEET_CLOSE_DURATION_MS)

    return () => window.clearTimeout(timeout)
  }, [canvasConfig, sheetOpen])

  // Close when navigating to another page, unless the new page opened a canvas
  // (deep link). Query-only changes are ignored — pages that sync canvas to
  // search params already call closeCanvas themselves.
  useEffect(() => {
    if (
      !shouldCloseCanvasAfterPathChange(
        pathRenderGenerationRef.current,
        openGenerationRef.current,
      )
    ) {
      return
    }
    setSheetOpen(false)
    setCanvasUiSurface(null)
  }, [pathname])

  const isOpen = sheetOpen

  const value = useMemo(
    () => ({
      openCanvas,
      closeCanvas,
      setCanvasSurface,
      isOpen,
    }),
    [closeCanvas, isOpen, openCanvas, setCanvasSurface]
  )

  return (
    <ArtifactCanvasContext.Provider value={value}>
      {children}
      {canvasConfig ? (
        <EditableArtifactCanvasShell
          open={sheetOpen}
          onOpenChange={handleOpenChange}
          portalContainer={portalContainer}
          title={canvasConfig.title}
          preventAutoFocus={canvasConfig.preventAutoFocus}
        >
          {canvasConfig.content}
        </EditableArtifactCanvasShell>
      ) : null}
    </ArtifactCanvasContext.Provider>
  )
}

export function useArtifactCanvas() {
  const context = useContext(ArtifactCanvasContext)

  if (!context) {
    throw new Error('useArtifactCanvas must be used within an ArtifactCanvasProvider')
  }

  return context
}
